import type { FC, MouseEventHandler } from 'react';
import { useCallback, useRef } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import MODALS from '@constants/modals';
import { useBlockFlags } from '@ContractBuilder/modules/block/context/context';
import { BlockContentDeleted } from '@ContractBuilder/modules/block-content/views/BlockContentDeleted';
import type { Attachment } from '@ContractBuilder/types';
import { AttachmentItem } from '@features/attachments/components/AttachmentItem';
import { BlockActionsController } from '@features/block-actions';
import { useDocumentLocation } from '@hooks/useDocumentLocation';
import { useModalDispatch } from '@hooks/useModalProvider';
import clsx from 'clsx';

interface InlineAttachmentProps {
  attachment: Attachment;
  isPDFRender: boolean;
  showBlockActions: boolean;
}

export const InlineAttachment: FC<InlineAttachmentProps> = ({ attachment, isPDFRender, showBlockActions = true }) => {
  const blockContentRef = useRef<HTMLDivElement>(null);
  const { showModal } = useModalDispatch();

  const { block, isHighlighted } = useBlockFlags();
  const blockId = block.id;

  const { isEndorsementView } = useDocumentLocation();

  const onEdit: MouseEventHandler = useCallback(
    () =>
      showModal(MODALS.ATTACHMENT_RENAME, {
        attachmentId: attachment.attachment_id,
        attachmentName: attachment?.name,
        attachmentFileName: attachment?.fileName,
      }),
    [attachment.attachment_id, attachment.name, attachment?.fileName, showModal],
  );

  const onDelete: MouseEventHandler = useCallback(
    () =>
      showModal(MODALS.CONFIRM_DELETE, {
        id: attachment.attachment_id,
        title: attachment?.name,
        type: 'attachment',
      }),
    [attachment.attachment_id, attachment.name, showModal],
  );

  return (
    <Show when={!isPDFRender}>
      <div
        data-block-type-attachment={attachment.id}
        id={blockId}
        className={clsx('transition duration-500', isHighlighted && 'bg-primary-50')}
      >
        <div className="relative">
          <Show when={showBlockActions}>
            <BlockActionsController handleDelete={onDelete} handleEdit={onEdit} blockContentRef={blockContentRef} />
          </Show>
          <div ref={blockContentRef} className={clsx('flex flex-col')}>
            <ShowFirstMatching>
              <Show when={(isEndorsementView || isPDFRender) && block?.isDeleted}>
                <BlockContentDeleted block={block} isHighlighted={isHighlighted} isPdfRender={isPDFRender} />
              </Show>
              <Show when={true}>
                <AttachmentItem
                  attachment={attachment}
                  key={`attachment-section-item-${attachment.attachment_id}`}
                  withContextMenu={false}
                />
              </Show>
            </ShowFirstMatching>
          </div>
        </div>
      </div>
    </Show>
  );
};
